// frontend/src/components/AdminPanel.js
import React, { useState, useEffect } from 'react';

const AdminPanel = ({ isOpen, onClose, onShowLocationOnMap }) => {   // ✅ onShowLocationOnMap ထည့်ပါ
    const [reports, setReports] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    // Fetch data when panel opens
    useEffect(() => {
        if (isOpen) {
            fetchSummary();
            fetchReports();
        }
    }, [isOpen, filter]);

    // Fetch summary statistics
    const fetchSummary = async () => {
        try {
            const response = await fetch('/api/admin/reports/summary');
            const data = await response.json();
            if (data.success) {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    // Fetch reports with filter
    const fetchReports = async () => {
        setLoading(true);
        try {
            const url = filter === 'all' ? '/api/reports' : `/api/reports?status=${filter}`;
            const response = await fetch(url);
            const data = await response.json();
            if (data.success) {
                setReports(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
        setLoading(false);
    };

    // Update report status
    const updateStatus = async (id, status) => {
        try {
            const response = await fetch(`/api/reports/${id}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            if (response.ok) {
                // Refresh data after update
                fetchSummary();
                fetchReports();
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('❌ Status ပြောင်းလဲရာတွင် အမှားရှိပါသည်။');
        }
    };

    // Get status badge style
    const getStatusBadge = (status) => {
        const badges = {
            pending: { color: '#f59e0b', label: '⏳ ဆိုင်းငံ့', bg: '#fef3c7' },
            in_progress: { color: '#3b82f6', label: '🔵 လုပ်ဆောင်ဆဲ', bg: '#dbeafe' },
            resolved: { color: '#22c55e', label: '🟢 ပြီးပြီ', bg: '#dcfce7' },
            rejected: { color: '#ef4444', label: '🔴 ပယ်ချ', bg: '#fee2e2' }
        };
        return badges[status] || badges.pending;
    };

    // Get report type label
    const getReportTypeLabel = (type) => {
        const types = {
            road: '🚧 လမ်းတံတားပျက်စီးမှု',
            trash: '🗑️ အမှိုက်ပုံပြဿနာ',
            electric: '💡 မီးလင်းရေးလိုအပ်ချက်',
            water: '💧 ရေပေးဝေရေးပြဿနာ',
            drainage: '🚿 ရေနုတ်မြောင်းပိတ်ဆို့ခြင်း',
            noise: '🔊 ဆူညံသံညစ်ညမ်းမှု',
            fire: '🔥 မီးလောင်မှု',
            other: '📝 အခြား'
        };
        return types[type] || type;
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('my-MM', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '520px',
            height: '100vh',
            background: '#0f172a',
            color: 'white',
            zIndex: 9999,
            padding: '24px',
            overflowY: 'auto',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            animation: 'slideInRight 0.3s ease'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #334155'
            }}>
                <div>
                    <h2 style={{ margin: 0, color: '#8b5cf6', fontSize: '20px' }}>📊 Admin Dashboard</h2>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
                        ပြည်သူ့အသံများကို စီမံခန့်ခွဲရန်
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background: '#ef4444',
                        border: 'none',
                        color: 'white',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                    ✕
                </button>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px',
                    marginBottom: '20px'
                }}>
                    <div style={{ background: '#1e293b', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#94a3b8' }}>{summary.total || 0}</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>📝 စုစုပေါင်း</div>
                    </div>
                    <div style={{ background: '#1e293b', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f59e0b' }}>{summary.pending || 0}</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>⏳ ဆိုင်းငံ့</div>
                    </div>
                    <div style={{ background: '#1e293b', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#3b82f6' }}>{summary.in_progress || 0}</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>🔵 လုပ်ဆောင်ဆဲ</div>
                    </div>
                    <div style={{ background: '#1e293b', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#22c55e' }}>{summary.resolved || 0}</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>🟢 ပြီးပြီ</div>
                    </div>
                    <div style={{ background: '#1e293b', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>{summary.rejected || 0}</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>🔴 ပယ်ချ</div>
                    </div>
                </div>
            )}

            {/* Filter Buttons */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {['all', 'pending', 'in_progress', 'resolved', 'rejected'].map((status) => (
                    <button
                        key={status}
                        onClick={() => setFilter(status)}
                        style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            border: filter === status ? '2px solid #8b5cf6' : '1px solid #334155',
                            background: filter === status ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                            color: filter === status ? '#8b5cf6' : '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: filter === status ? '600' : '400',
                            transition: 'all 0.2s'
                        }}
                    >
                        {status === 'all' && '📋 အားလုံး'}
                        {status === 'pending' && '⏳ ဆိုင်းငံ့'}
                        {status === 'in_progress' && '🔵 လုပ်ဆောင်ဆဲ'}
                        {status === 'resolved' && '🟢 ပြီးပြီ'}
                        {status === 'rejected' && '🔴 ပယ်ချ'}
                        {filter === status && ` (${reports.length})`}
                    </button>
                ))}
            </div>

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    <div style={{ fontSize: '30px', marginBottom: '8px' }}>⏳</div>
                    <div>ဒေတာများကို ရယူနေပါသည်...</div>
                </div>
            )}

            {/* Reports List */}
            {!loading && (
                <div>
                    {reports.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#94a3b8',
                            background: '#1e293b',
                            borderRadius: '8px'
                        }}>
                            <div style={{ fontSize: '30px', marginBottom: '8px' }}>📭</div>
                            <div>အစီရင်ခံစာမရှိပါ</div>
                        </div>
                    ) : (
                        reports.map((report) => {
                            const statusBadge = getStatusBadge(report.status);
                            return (
                                <div
                                    key={report.id}
                                    style={{
                                        background: '#1e293b',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '12px',
                                        borderLeft: `4px solid ${statusBadge.color}`,
                                        transition: 'transform 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>
                                                {getReportTypeLabel(report.report_type)}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '6px' }}>
                                                {report.description || 'အကြောင်းအရာမရှိပါ'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                <span>📅 {formatDate(report.created_at)}</span>
                                                {report.location_name && <span>📍 {report.location_name}</span>}
                                                {report.phone && <span>📞 {report.phone}</span>}
                                                {report.reporter_name && <span>👤 {report.reporter_name}</span>}
                                            </div>
                                        </div>
                                        <select
                                            value={report.status}
                                            onChange={(e) => updateStatus(report.id, e.target.value)}
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '4px',
                                                background: '#0f172a',
                                                border: '1px solid #334155',
                                                color: 'white',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                minWidth: '110px',
                                                marginLeft: '8px'
                                            }}
                                        >
                                            <option value="pending">⏳ ဆိုင်းငံ့</option>
                                            <option value="in_progress">🔵 လုပ်ဆောင်ဆဲ</option>
                                            <option value="resolved">🟢 ပြီးပြီ</option>
                                            <option value="rejected">🔴 ပယ်ချ</option>
                                        </select>
                                    </div>
                                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <span style={{
                                            background: statusBadge.bg,
                                            color: statusBadge.color,
                                            padding: '2px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '500'
                                        }}>
                                            {statusBadge.label}
                                        </span>
                                         {/* ✅ ဒီနေရာမှာ ပြင်ဆင်ပါ */}
              {report.lat && report.lng && (
                <button
                  onClick={() => {
                    // ✅ Admin Panel ကိုပိတ်ပြီး Map ပေါ်မှာ ပြမယ်
                    onClose();
                    onShowLocationOnMap({
                    lat: Number(report.lat),      // ✅ Number
                    lng: Number(report.lng),      // ✅ Number
                      name_mm: report.location_name || 'ပြည်သူ့အသံ တည်နေရာ',
                      description: report.description || ''
                    });
                  }}
                                                style={{
                                                    color: '#3b82f6',
                                                    fontSize: '11px',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                🗺️ မြေပုံကြည့်ရန်
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Footer */}
            <div style={{
                marginTop: '16px',
                paddingTop: '12px',
                borderTop: '1px solid #334155',
                fontSize: '12px',
                color: '#64748b',
                textAlign: 'center'
            }}>
                📊 စုစုပေါင်း အစီရင်ခံစာ: {reports.length} ခု
                {filter !== 'all' && ` (${filter === 'pending' ? 'ဆိုင်းငံ့' : filter === 'in_progress' ? 'လုပ်ဆောင်ဆဲ' : filter === 'resolved' ? 'ပြီးပြီ' : 'ပယ်ချ'})`}
            </div>

            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

export default AdminPanel;